import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import { ButtonsGroup } from '@monorepo/shared/componentsV2/ButtonsGroup';
import { DateRangeFilterButton } from '@monorepo/shared/componentsV2/DateRangeFilterButton';
import { MenuButton } from '@monorepo/shared/componentsV2/menus/MenuButton';
import { TableBulkActions } from '@monorepo/shared/componentsV2/Table/TableBulkActions';
import { useTableBulkActions } from '@monorepo/shared/componentsV2/Table/useTableBulkActions';
import { Toolbar } from '@monorepo/shared/componentsV2/Toolbar';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { useIsLogAiEnabled } from '@monorepo/shared/hooks/logs/useIsLogAiEnabled';
import { useHasProjectUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useOpenClose } from '@monorepo/shared/hooks/useOpenClose';
import { linkTarget } from '@monorepo/shared/utils/linkTarget';
import DownloadIcon from '@svg/download.svg';
import VisibilityOffIcon from '@svg/visibility-off.svg';
import VisibilityOnIcon from '@svg/visibility-on.svg';
import * as Sentry from '@sentry/browser';
import { MAPLE_AI_EMAIL_DOMAIN } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { LogEntryExportModal } from './LogEntryExportModal';

const StyledToolbar = styled(Toolbar)`
  & .MuiToolbar-root {
    padding-right: 1rem;
    padding-left: 1rem;
  }
`;

const StyledTableBulkActions = styled(TableBulkActions)`
  padding-left: 0.625rem;
`;

const StyledExportIconButton = styled(IconButton)`
  width: 34px;
  height: 34px;

  & svg {
    width: 16px;
    height: 16px;
    stroke: ${({ theme }) => theme.colors.gray666};
  }
`;

const StyledShowHideDescriptionButton = styled(IconButton)`
  width: 34px;
  height: 34px;

  & svg {
    width: 14px;
    height: 14px;
    fill: ${({ theme }) => theme.colors.gray666};
  }
`;

function getMapleAiEmailAddress(logId: string, projectId: string): string {
  const prefix = process.env.MAPLE_AI_EMAIL_PREFIX;
  if (!prefix) {
    Sentry.captureException('MAPLE_AI_EMAIL_PREFIX is not configured');
    throw new Error('MAPLE_AI_EMAIL_PREFIX is not configured');
  }
  return `${prefix}+${logId}+${projectId}@${MAPLE_AI_EMAIL_DOMAIN}`;
}

type LogEntryToolbarProps = Omit<
  React.ComponentProps<typeof Toolbar>,
  'rightActions' | 'leftActions'
> & {
  isBulkDeleting: boolean;
  logId: string;
  organizationId: string;
  projectId: string;
  onAddNew: () => void;
  onBulkDelete: () => void;
  onUpload: () => void;
  isFieldDescriptionsShown: boolean;
  handleIsFieldDescriptionsShown: () => void;
};

export function LogEntryToolbar({
  isBulkDeleting,
  logId,
  onAddNew,
  onBulkDelete,
  onUpload,
  organizationId,
  projectId,
  isFieldDescriptionsShown,
  handleIsFieldDescriptionsShown,
  ...props
}: LogEntryToolbarProps) {
  const canSubmitDataForProject = useHasProjectUpdatePermissions(projectId);
  const { isBulkActionsModeOn } = useTableBulkActions();

  const [exportModalIsOpen, handleOpenExportModal, closeExportModal] =
    useOpenClose();

  const isLogAiEnabled = useIsLogAiEnabled(organizationId);

  const onEmailSubmission = useCallback(() => {
    const emailAddress = getMapleAiEmailAddress(logId, projectId);
    window.open(
      `mailto:${emailAddress}`,
      linkTarget.NEW_BROWSER_TAB,
      'noopener,noreferrer',
    );
  }, [logId, projectId]);

  const menuButtonOptions = useMemo(
    () => [
      { onClick: onAddNew, displayName: 'Add manually' },
      { onClick: onUpload, displayName: 'Upload files' },
      ...(isLogAiEnabled
        ? [{ onClick: onEmailSubmission, displayName: 'Email submission' }]
        : []),
    ],
    [onAddNew, onUpload, onEmailSubmission, isLogAiEnabled],
  );

  return (
    <>
      <StyledToolbar
        leftActions={
          canSubmitDataForProject ? (
            <StyledTableBulkActions
              isBulkDeleting={isBulkDeleting}
              onDelete={onBulkDelete}
            />
          ) : null
        }
        rightActions={
          <ButtonsGroup>
            <DateRangeFilterButton />
            <Tooltip
              title={`${
                isFieldDescriptionsShown ? 'Hide' : 'Show'
              } full field descriptions`}
            >
              <StyledShowHideDescriptionButton
                onClick={handleIsFieldDescriptionsShown}
              >
                {isFieldDescriptionsShown ? (
                  <VisibilityOnIcon />
                ) : (
                  <VisibilityOffIcon />
                )}
              </StyledShowHideDescriptionButton>
            </Tooltip>
            {canSubmitDataForProject && !isBulkActionsModeOn ? (
              <>
                <Tooltip title="Export">
                  <StyledExportIconButton onClick={handleOpenExportModal}>
                    <DownloadIcon />
                  </StyledExportIconButton>
                </Tooltip>
                <MenuButton
                  label="Add Log data"
                  options={menuButtonOptions}
                  menuWidthShouldMatchButtonEl
                />
              </>
            ) : null}
          </ButtonsGroup>
        }
        size="small"
        variant="noDropShadow"
        {...props}
      />
      {
        // reset the modal each time it reopens
        exportModalIsOpen && (
          <LogEntryExportModal
            open={exportModalIsOpen}
            onClose={closeExportModal}
            logId={logId}
            organizationId={organizationId}
            projectId={projectId}
          />
        )
      }
    </>
  );
}
